import { doc, setDoc, writeBatch } from 'firebase/firestore'
import { requireAuth, requireDb } from '../firebase'
import { loadSnapshot, saveSnapshot, updateItem, updateListMetadata } from '../data'
import type { CloudLayout, CloudShoppingItem, CloudShoppingList, CloudStore, Language } from '../models'

const now = () => Date.now()
const makeId = () => crypto.randomUUID()
const queue = (operation: Promise<unknown>, label: string) => {
  void operation.catch((error) => console.error(`Shelfway ${label} sync failed`, error))
}

export async function saveStore(store: CloudStore): Promise<CloudStore> {
  const next = { ...store, updatedAt: now() }
  const ownerUid = next.ownerUserId ?? requireAuth().currentUser?.uid
  if (!ownerUid) throw new Error('Sign in first.')
  await setDoc(doc(requireDb(), 'stores', next.id), {
    name: next.name,
    city: next.city ?? null,
    created_by: ownerUid,
    updated_at: next.updatedAt,
    payload: JSON.stringify({ ...next, ownerUserId: ownerUid }),
  }, { merge: true })
  return { ...next, ownerUserId: ownerUid }
}

export function addManualItem(
  list: CloudShoppingList,
  title: string,
  suffix: string,
  categoryId: string,
): CloudShoppingItem {
  const timestamp = now()
  const item: CloudShoppingItem = {
    id: makeId(),
    displayTitle: title.trim(),
    originalInput: title.trim(),
    quantityText: null,
    note: suffix.trim() || null,
    isChecked: false,
    checkedAt: null,
    categoryId,
    recommendationSource: 'manual',
    recommendationConfidence: 'strong',
    manualSortPosition: Math.max(-1, ...list.items.map((entry) => entry.manualSortPosition)) + 1,
    createdAt: timestamp,
    updatedAt: timestamp,
    deletedAt: null,
  }
  queue(setDoc(doc(requireDb(), 'lists', list.id, 'items', item.id), {
    updated_at: item.updatedAt,
    deleted_at: null,
    payload: JSON.stringify(item),
  }, { merge: true }), 'add manual item')
  return item
}

export function addDetailedItems(
  list: CloudShoppingList,
  rawItems: Array<Record<string, unknown>>,
  allowedCategoryIds: Set<string>,
): CloudShoppingItem[] {
  const db = requireDb()
  const batch = writeBatch(db)
  const base = now()
  const start = Math.max(-1, ...list.items.map((entry) => entry.manualSortPosition)) + 1
  const result: CloudShoppingItem[] = []

  rawItems.forEach((raw, index) => {
    const title = String(raw.title ?? raw.displayTitle ?? '').trim()
    if (!title) return
    const suffix = String(raw.suffix ?? raw.note ?? raw.quantity ?? '').trim()
    const requestedCategory = String(raw.category_id ?? raw.categoryId ?? '').trim()
    const categoryId = allowedCategoryIds.size === 0 || allowedCategoryIds.has(requestedCategory)
      ? (requestedCategory || null)
      : null
    const timestamp = base + index
    const item: CloudShoppingItem = {
      id: makeId(),
      displayTitle: title,
      originalInput: title,
      quantityText: null,
      note: suffix || null,
      isChecked: Boolean(raw.checked),
      checkedAt: raw.checked ? timestamp : null,
      categoryId,
      recommendationSource: categoryId ? 'manual' : null,
      recommendationConfidence: categoryId ? 'strong' : null,
      manualSortPosition: start + index,
      createdAt: timestamp,
      updatedAt: timestamp,
      deletedAt: null,
    }
    result.push(item)
    batch.set(doc(db, 'lists', list.id, 'items', item.id), {
      updated_at: item.updatedAt,
      deleted_at: null,
      payload: JSON.stringify(item),
    }, { merge: true })
  })

  if (result.length) queue(batch.commit(), 'import detailed items')
  return result
}

export async function clearCompletedItems(list: CloudShoppingList) {
  const checked = list.items.filter((item) => item.isChecked)
  await Promise.all(checked.map((item) => updateItem(list.id, item, { deletedAt: now() })))
}

export async function setStoreArchived(uid: string, storeId: string, archived: boolean) {
  const snapshot = await loadSnapshot(uid)
  const ids = new Set(snapshot.archivedStoreIds ?? [])
  if (archived) ids.add(storeId); else ids.delete(storeId)
  await saveSnapshot(uid, { ...snapshot, archivedStoreIds: [...ids], updatedAt: now() }, requireAuth().currentUser?.email)
  return [...ids]
}

export async function setLayoutArchived(uid: string, layoutId: string, archived: boolean) {
  const snapshot = await loadSnapshot(uid)
  const ids = new Set(snapshot.archivedLayoutIds ?? [])
  if (archived) ids.add(layoutId); else ids.delete(layoutId)
  await saveSnapshot(uid, { ...snapshot, archivedLayoutIds: [...ids], updatedAt: now() }, requireAuth().currentUser?.email)
  return [...ids]
}

export async function archiveList(list: CloudShoppingList) {
  await updateListMetadata({ ...list, status: 'archived' })
}

export async function restoreList(list: CloudShoppingList) {
  await updateListMetadata({ ...list, status: 'active', completedAt: null })
}

export async function applyRetargetJson(
  list: CloudShoppingList,
  targetStoreId: string | null,
  targetLayout: CloudLayout | null,
  jsonText: string,
) {
  const root = JSON.parse(jsonText) as { items?: Array<Record<string, unknown>> }
  if (!Array.isArray(root.items)) throw new Error('JSON must contain an items array.')
  const items = [...list.items].sort((a, b) => a.manualSortPosition - b.manualSortPosition || a.createdAt - b.createdAt)
  const allowed = new Set(targetLayout?.sections.map((section) => section.categoryId) ?? [])
  const seen = new Set<number>()
  const updates: Promise<void>[] = []

  for (const raw of root.items) {
    const sourceIndex = Number(raw.source_index)
    if (!Number.isInteger(sourceIndex) || sourceIndex < 0 || sourceIndex >= items.length || seen.has(sourceIndex)) {
      throw new Error('Every item must contain each source_index exactly once.')
    }
    const categoryId = String(raw.category_id ?? '').trim()
    if (targetLayout && !allowed.has(categoryId)) throw new Error(`Unknown target category: ${categoryId}`)
    seen.add(sourceIndex)
    updates.push(updateItem(list.id, items[sourceIndex], { categoryId: categoryId || null }))
  }
  if (seen.size !== items.length) throw new Error('The recategorized JSON does not contain every list item.')
  await Promise.all(updates)
  await updateListMetadata({ ...list, storeId: targetStoreId, layoutId: targetLayout?.id ?? null })
}

export function buildCreateListPrompt(
  language: Language,
  listName: string,
  store: CloudStore | null,
  layout: CloudLayout | null,
) {
  const categories = layout?.sections
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((section) => `- ${section.categoryId}: ${section.categoryNames[language] || section.categoryNames.en || section.categoryNames.sv || section.categoryId}`)
    .join('\n') || '- No shopping-order categories selected'
  return `You are converting a plain shopping list into SHELFWAY JSON.\n\nSHELFWAY sorts shopping items by the category order in a selected store layout. Do not invent category IDs and do not return markdown.\n\nRules:\n- Preserve every requested item.\n- Keep title concise.\n- Optional amount, variant, owner, size, or note belongs in suffix as one opaque string.\n- checked must always be false.\n- category_id must use one of the allowed IDs below when a shopping order is selected.\n\nLanguage: ${language}\nList name: ${listName.trim() || (language === 'sv' ? 'Inköpslista' : 'Shopping list')}\nStore: ${store ? `${store.name}${store.city ? `, ${store.city}` : ''}` : 'None'}\nLayout: ${layout?.name ?? 'None'}\n\nALLOWED CATEGORIES:\n${categories}\n\nReturn exactly one JSON object with:\n{\n  "schema": "aisle.shopping-list",\n  "version": 1,\n  "language": "${language}",\n  "list": { "name": "...", "store_id": ${store ? JSON.stringify(store.id) : 'null'}, "layout_id": ${layout ? JSON.stringify(layout.id) : 'null'} },\n  "items": [{ "title": "...", "suffix": "optional", "category_id": "...", "checked": false }]\n}`
}

export function buildAppendItemsPrompt(language: Language, list: CloudShoppingList, layout: CloudLayout | null) {
  const categories = layout?.sections
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((section) => `- ${section.categoryId}: ${section.categoryNames[language] || section.categoryNames.en || section.categoryNames.sv || section.categoryId}`)
    .join('\n') || '- No fixed categories'
  return `You are adding items to an EXISTING SHELFWAY shopping list. Return exactly one JSON object and no markdown.\n\nList: ${list.name}\nLanguage: ${language}\n\nAllowed categories:\n${categories}\n\nReturn:\n{\n  "schema": "aisle.shopping-list",\n  "version": 1,\n  "language": "${language}",\n  "items": [{ "title": "...", "suffix": "optional", "category_id": "...", "checked": false }]\n}`
}

export function buildStoreLayoutPrompt(language: Language, storeName: string, city: string, layoutName: string) {
  return `You are creating a physical store shopping order for SHELFWAY from a store map, a written walking order, or both.\n\nInfer the order a shopper encounters sections from entrance to checkout. Return exactly one JSON object and no markdown. Keep section names practical and include each section once.\n\nLanguage: ${language}\nStore: ${storeName || 'Unknown'}\nCity: ${city || 'Unknown'}\nDesired layout name: ${layoutName || (language === 'sv' ? 'Min butiksordning' : 'My shopping order')}\n\nReturn:\n{\n  "schema": "aisle.store-layout",\n  "version": 1,\n  "language": "${language}",\n  "store": { "name": ${JSON.stringify(storeName)}, "city": ${JSON.stringify(city)} },\n  "layout": { "name": ${JSON.stringify(layoutName)}, "sections": [{ "name": "Fruit & vegetables" }] }\n}`
}

export function buildRetargetPrompt(language: Language, list: CloudShoppingList, targetStore: CloudStore | null, targetLayout: CloudLayout) {
  const orderedItems = [...list.items].sort((a, b) => a.manualSortPosition - b.manualSortPosition || a.createdAt - b.createdAt)
  const categories = targetLayout.sections.slice().sort((a, b) => a.displayOrder - b.displayOrder)
    .map((section) => `- ${section.categoryId}: ${section.categoryNames[language] || section.categoryNames.en || section.categoryNames.sv || section.categoryId}`)
    .join('\n')
  const existing = orderedItems.map((item, index) => `${index}. ${item.displayTitle}${(item.note || item.quantityText) ? ` · ${item.note || item.quantityText}` : ''}`).join('\n')
  return `You are recategorizing an EXISTING SHELFWAY shopping list for a different store/shopping order.\n\nSTRICT RULES:\n- Return exactly one JSON object and nothing else.\n- Keep every existing item exactly once.\n- source_index is the stable identity. Include every source_index from 0 through ${Math.max(0, orderedItems.length - 1)} exactly once.\n- Change only category_id.\n- category_id MUST be one of the target category IDs.\n\nLanguage: ${language}\nTarget store: ${targetStore?.name ?? 'None'}\nTarget shopping order: ${targetLayout.name}\n\nEXISTING ITEMS:\n${existing}\n\nTARGET SHOPPING ORDER CATEGORIES:\n${categories}\n\nReturn:\n{ "items": [{ "source_index": 0, "category_id": "..." }] }`
}
